import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { ListingFilters } from '../components/filters/ListingFilters';
import { API_BASE } from '../config/api';

export const Programs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [universities, setUniversities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Unified filter state - Initialize from URL params
  const [filters, setFilters] = useState({
    selectedField: searchParams.get('field') || '',
    selectedSubjects: searchParams.get('subjects') ? searchParams.get('subjects').split(',') : [],
    searchQuery: searchParams.get('search') || '',
    selectedCountry: searchParams.get('country') || '',
    selectedLevel: searchParams.get('level') || '',
    selectedMode: searchParams.get('mode') || '',
    showCashbackOnly: searchParams.get('cashback') === 'true',
    showScholarshipOnly: searchParams.get('scholarship') === 'true'
  });
  
  // UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileDisplayCount, setMobileDisplayCount] = useState(9);
  const [desktopExpandedRows, setDesktopExpandedRows] = useState(1);
  
  const backendUrl = API_BASE;
  const DESKTOP_ITEMS_PER_ROW = 4;
  const DESKTOP_ROWS_PER_PAGE = 4;
  const DESKTOP_PER_PAGE = DESKTOP_ITEMS_PER_ROW * DESKTOP_ROWS_PER_PAGE; // 16
  const MOBILE_LOAD_MORE = 9;

  // Fetch programs and universities
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch programs and universities in parallel
        const [programsResponse, universitiesResponse] = await Promise.all([
          fetch(`${backendUrl}/api/public/programs`),
          fetch(`${backendUrl}/api/public/universities`)
        ]);
        
        if (!programsResponse.ok) throw new Error('Failed to fetch programs');
        if (!universitiesResponse.ok) throw new Error('Failed to fetch universities');
        
        const programsData = await programsResponse.json();
        const universitiesData = await universitiesResponse.json();
        
        // Create university lookup map
        const universityMap = {};
        (universitiesData.universities || []).forEach(uni => {
          universityMap[uni.id] = uni.name;
        });
        
        // Add university_name to each program
        const programsWithUniversity = (programsData.programs || []).map(prog => {
          if (!prog || !prog.id) {
            console.warn('Skipping invalid program:', prog);
            return null;
          }
          
          // Normalize level_of_study: backend returns 'level_of_study' (string), but handle arrays too
          let levelOfStudy = '';
          if (Array.isArray(prog.levels_of_study) && prog.levels_of_study.length > 0) {
            levelOfStudy = prog.levels_of_study[0];
          } else if (prog.level_of_study) {
            levelOfStudy = Array.isArray(prog.level_of_study) ? prog.level_of_study[0] : prog.level_of_study;
          }
          
          // Normalize mode_of_study: backend returns 'mode_of_study' (string), but handle arrays too
          let modeOfStudy = '';
          if (Array.isArray(prog.modes_of_study) && prog.modes_of_study.length > 0) {
            modeOfStudy = prog.modes_of_study[0];
          } else if (prog.mode_of_study) {
            modeOfStudy = Array.isArray(prog.mode_of_study) ? prog.mode_of_study[0] : prog.mode_of_study;
          }
          
          return {
            ...prog,
            university_name: universityMap[prog.university_id] || 'Unknown University',
            country: universitiesData.universities?.find(u => u.id === prog.university_id)?.country || '',
            scholarship_available: !!(prog.cashback_amount && Number(prog.cashback_amount) > 0),
            is_featured: prog.id === '07c3fe0a-c5d8-42a6-b24b-9ac612763d05' || 
                         prog.id === '8edbe8ac-87ed-4c41-8c1a-b5bbbc685d52' || 
                         prog.id === '20ddc1c8-db94-4d2d-b5ad-68fc94cb2f27',
            field_of_study: prog.field_of_study_primary || (Array.isArray(prog.fields_of_study) && prog.fields_of_study.length > 0 ? prog.fields_of_study[0] : '') || 'General',
            subject: (Array.isArray(prog.subject_tags) && prog.subject_tags.length > 0) ? prog.subject_tags[0] : 'General',
            // Normalize for filtering and display - use string for compatibility
            level_of_study: levelOfStudy,
            mode_of_study: modeOfStudy,
            // Keep arrays for potential future use
            levels_of_study: Array.isArray(prog.levels_of_study) ? prog.levels_of_study : (levelOfStudy ? [levelOfStudy] : []),
            modes_of_study: Array.isArray(prog.modes_of_study) ? prog.modes_of_study : (modeOfStudy ? [modeOfStudy] : [])
          };
        }).filter(Boolean); // Remove any null entries
        
        setUniversities(universityMap);
        setPrograms(programsWithUniversity);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [backendUrl]);

  // Filter programs (memoized to avoid recalculation on every render)
  const filteredPrograms = useMemo(() => {
    // Defensive check: ensure programs is an array
    if (!Array.isArray(programs)) {
      return [];
    }
    
    return programs.filter(program => {
      // Skip invalid programs
      if (!program || !program.id) {
        return false;
      }
      if (filters.searchQuery && program.program_name && !program.program_name.toLowerCase().includes(filters.searchQuery.toLowerCase())) {
        return false;
      }
      
      if (filters.selectedField && program.field_of_study !== filters.selectedField) {
        return false;
      }
      
      if (filters.selectedSubjects.length > 0 && !filters.selectedSubjects.includes(program.subject)) {
        return false;
      }
      
      if (filters.selectedCountry && program.country !== filters.selectedCountry) {
        return false;
      }
      
      if (filters.selectedLevel) {
        // Handle both string and array formats for level_of_study
        const programLevels = Array.isArray(program.level_of_study) 
          ? program.level_of_study 
          : (Array.isArray(program.levels_of_study) ? program.levels_of_study : [program.level_of_study].filter(Boolean));
        if (!programLevels.includes(filters.selectedLevel) && program.level_of_study !== filters.selectedLevel) {
          return false;
        }
      }
      
      if (filters.selectedMode) {
        // Handle both string and array formats for mode_of_study
        const programModes = Array.isArray(program.mode_of_study) 
          ? program.mode_of_study 
          : (Array.isArray(program.modes_of_study) ? program.modes_of_study : [program.mode_of_study].filter(Boolean));
        if (!programModes.includes(filters.selectedMode) && program.mode_of_study !== filters.selectedMode) {
          return false;
        }
      }
      
      if (filters.showCashbackOnly && !program.cashback_amount) {
        return false;
      }
      
      if (filters.showScholarshipOnly && !program.scholarship_available) {
        return false;
      }
      
      return true;
    });
  }, [programs, filters]);

  // Featured programs logic - Always show featured programs first (memoized)
  const { sortedPrograms, featuredProgramsIds } = useMemo(() => {
    try {
      // Defensive check: ensure filteredPrograms is an array
      if (!Array.isArray(filteredPrograms)) {
        return { sortedPrograms: [], featuredProgramsIds: new Set() };
      }
      
      const featuredPrograms = filteredPrograms.filter(p => p && p.id && p.is_featured);
      const nonFeaturedPrograms = filteredPrograms.filter(p => p && p.id && !p.is_featured);
      const sorted = [...featuredPrograms, ...nonFeaturedPrograms];
      const featuredIds = new Set(featuredPrograms.map(p => p.id).filter(Boolean));
      return { sortedPrograms: sorted, featuredProgramsIds: featuredIds };
    } catch (err) {
      console.error('Error sorting programs:', err);
      return { sortedPrograms: [], featuredProgramsIds: new Set() };
    }
  }, [filteredPrograms]);

  // Ensure sortedPrograms is always an array (safety check)
  const safeSortedPrograms = Array.isArray(sortedPrograms) ? sortedPrograms : [];
  
  // Pagination
  const totalPrograms = safeSortedPrograms.length;
  const totalPages = Math.ceil(totalPrograms / DESKTOP_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * DESKTOP_PER_PAGE;
  const endIndex = startIndex + DESKTOP_PER_PAGE;
  const paginatedPrograms = safeSortedPrograms.slice(startIndex, endIndex);
  
  // Desktop expandable view
  const displayedRowsCount = desktopExpandedRows * DESKTOP_ROWS_PER_PAGE * DESKTOP_ITEMS_PER_ROW;
  const currentPagePrograms = safeSortedPrograms.slice(startIndex, startIndex + displayedRowsCount);
  const canLoadMore = displayedRowsCount < endIndex && currentPagePrograms.length < paginatedPrograms.length;
  
  const mobilePrograms = safeSortedPrograms.slice(0, mobileDisplayCount);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setMobileDisplayCount(9);
    setDesktopExpandedRows(1);
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    
    // Update URL params
    const params = {};
    if (newFilters.searchQuery) params.search = newFilters.searchQuery;
    if (newFilters.selectedField) params.field = newFilters.selectedField;
    if (newFilters.selectedSubjects && newFilters.selectedSubjects.length > 0) {
      params.subjects = newFilters.selectedSubjects.join(',');
    }
    if (newFilters.selectedCountry) params.country = newFilters.selectedCountry;
    if (newFilters.selectedLevel) params.level = newFilters.selectedLevel;
    if (newFilters.selectedMode) params.mode = newFilters.selectedMode;
    if (newFilters.showCashbackOnly) params.cashback = 'true';
    if (newFilters.showScholarshipOnly) params.scholarship = 'true';
    setSearchParams(params);
  };

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789', '_blank');
  };

  const handleLoadMoreMobile = () => {
    setMobileDisplayCount(prev => prev + MOBILE_LOAD_MORE);
  };

  const handleLoadMoreRows = () => {
    setDesktopExpandedRows(prev => prev + 1);
  };

  const hasActiveFilters = 
    filters.selectedField || 
    filters.selectedSubjects.length > 0 || 
    filters.searchQuery || 
    filters.selectedCountry || 
    filters.selectedLevel || 
    filters.selectedMode || 
    filters.showCashbackOnly || 
    filters.showScholarshipOnly;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">Unable to Load Programs</h1>
            <p className="text-[#4B5563] mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const ModernPagination = ({ position = 'bottom' }) => (
    <div className={`hidden md:flex items-center gap-3 ${position === 'top' ? 'justify-end' : 'justify-center'}`}>
      <button
        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
        disabled={currentPage === 1}
        className="w-10 h-10 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-[#D1D5DB] transition-all shadow-sm"
        data-testid="prev-page-button"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <div className="flex items-center gap-1">
        {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = idx + 1;
          } else if (currentPage <= 3) {
            pageNum = idx + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + idx;
          } else {
            pageNum = currentPage - 2 + idx;
          }
          return (
            <button
              key={pageNum}
              onClick={() => {
                setCurrentPage(pageNum);
                setDesktopExpandedRows(1);
              }}
              className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-all ${
                currentPage === pageNum
                  ? 'bg-[#111827] text-white shadow-md'
                  : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF]'
              }`}
              data-testid={`page-${pageNum}-button`}
            >
              {pageNum}
            </button>
          );
        })}
      </div>
      
      <button
        onClick={() => {
          setCurrentPage(prev => Math.min(totalPages, prev + 1));
          setDesktopExpandedRows(1);
        }}
        disabled={currentPage === totalPages}
        className="w-10 h-10 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-[#D1D5DB] transition-all shadow-sm"
        data-testid="next-page-button"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {position === 'top' && (
        <span className="ml-2 text-sm text-[#6B7280]">
          Page <span className="font-semibold text-[#111827]">{currentPage}</span> of <span className="font-semibold text-[#111827]">{totalPages}</span>
        </span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <Header />
      
      <main className="flex-1 px-4 md:px-6 lg:px-8">
        {/* PAGE HEADER */}
        <section className="py-10 md:py-12 bg-white rounded-2xl mt-6 shadow-sm">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#111827] mb-3">
              Explore Programs & Courses
            </h1>
            <p className="text-base md:text-lg text-[#4B5563] max-w-3xl mx-auto">
              Compare programs, fees, and opportunities across universities. Make informed decisions with our advisory guidance.
            </p>
          </div>
        </section>

        {/* UNIFIED FILTER COMPONENT - Replaces old filter sections */}
        <ListingFilters 
          context="programs"
          filters={filters}
          onFilterChange={handleFilterChange}
          resultCount={totalPrograms}
        />

        {/* PROGRAM LISTING */}
        <section className="py-10 md:py-12 bg-white rounded-2xl mt-4 shadow-sm transition-all duration-300">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            {safeSortedPrograms.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F8FAFC] rounded-full mb-4">
                  <svg className="w-8 h-8 text-[#E5E7EB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-2">No Programs Found</h3>
                <p className="text-[#4B5563] mb-6 max-w-md mx-auto text-sm">Not sure which program fits? Our advisors can help.</p>
                <button
                  onClick={handleConsultClick}
                  className="px-6 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-xl transition-all text-sm"
                  data-testid="consult-us-button"
                >
                  Consult Us
                </button>
              </div>
            ) : (
              <>
                {/* Top Pagination - Desktop Only */}
                <div className="hidden md:flex justify-between items-center mb-6">
                  <p className="text-sm text-[#6B7280]">
                    Showing <span className="font-semibold text-[#111827]">{startIndex + 1}–{Math.min(endIndex, totalPrograms)}</span> of <span className="font-semibold text-[#111827]">{totalPrograms}</span>
                  </p>
                  <ModernPagination position="top" />
                </div>

                {/* Desktop: 4 Column Grid */}
                <div className="hidden md:grid md:grid-cols-4 gap-4 mb-6">
                  {currentPagePrograms.map(program => (
                    <ProgramCard key={program.id} program={program} isFeatured={featuredProgramsIds.has(program.id)} navigate={navigate} />
                  ))}
                </div>

                {/* Desktop: Load More Rows Button */}
                {canLoadMore && (
                  <div className="hidden md:flex justify-center mb-6">
                    <button
                      onClick={handleLoadMoreRows}
                      className="px-6 py-3 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-gradient-to-r hover:from-[#F97316] hover:to-[#fb7e2e] hover:text-white font-semibold rounded-xl transition-all flex items-center gap-2"
                    >
                      View More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Mobile: Single Column */}
                <div className="md:hidden grid grid-cols-1 gap-3 mb-6">
                  {mobilePrograms.map(program => (
                    <ProgramCard key={program.id} program={program} isFeatured={featuredProgramsIds.has(program.id)} mobile navigate={navigate} />
                  ))}
                </div>

                {/* Mobile: Load More Button */}
                {mobileDisplayCount < totalPrograms && (
                  <div className="md:hidden text-center mb-6">
                    <button
                      onClick={handleLoadMoreMobile}
                      className="px-8 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all"
                      data-testid="load-more-button"
                    >
                      Load {Math.min(MOBILE_LOAD_MORE, totalPrograms - mobileDisplayCount)} More Programs
                    </button>
                  </div>
                )}

                {/* Mobile Result Count */}
                <p className="md:hidden text-center text-xs text-[#6B7280] mb-6">
                  Showing <span className="font-semibold text-[#111827]">{Math.min(mobileDisplayCount, totalPrograms)}</span> of <span className="font-semibold text-[#111827]">{totalPrograms}</span>
                </p>
              </>
            )}
          </div>
        </section>

        {/* Bottom Pagination */}
        {safeSortedPrograms.length > 0 && (
          <section className="py-6 bg-white rounded-2xl mt-4 shadow-sm">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
              <ModernPagination position="bottom" />
            </div>
          </section>
        )}

        {/* AD BANNER (Above Footer) */}
        <section className="py-10 bg-white rounded-2xl mt-4 mb-6 shadow-sm">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <a
              href="#"
              className="block rounded-2xl overflow-hidden hover:opacity-95 transition-opacity shadow-sm"
            >
              <img
                src="/placeholders/image-generic.svg"
                alt="Advertisement"
                className="w-full h-auto"
              />
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message="Hi, I would like to consult about programs." />
    </div>
  );
};

// Program Card Component
const ProgramCard = ({ program, isFeatured, mobile = false, navigate }) => {
  // Defensive normalization of data to prevent crashes
  if (!program || !program.id) {
    console.error('ProgramCard: Invalid program data', program);
    return null;
  }
  
  // Safely extract display values
  const programName = program.program_name || 'Program';
  const universityName = program.university_name || 'Unknown University';
  const levelDisplay = program.level_of_study 
    ? (Array.isArray(program.level_of_study) ? program.level_of_study[0] : program.level_of_study)
    : (Array.isArray(program.levels_of_study) && program.levels_of_study.length > 0 ? program.levels_of_study[0] : '');
  const modeDisplay = program.mode_of_study 
    ? (Array.isArray(program.mode_of_study) ? program.mode_of_study[0] : program.mode_of_study)
    : (Array.isArray(program.modes_of_study) && program.modes_of_study.length > 0 ? program.modes_of_study[0] : '');
  const duration = program.duration || '';
  
  // Safe number formatting
  const formatFee = (fee) => {
    if (!fee && fee !== 0) return null;
    if (typeof fee === 'number') return fee.toLocaleString();
    const numFee = Number(fee);
    return !isNaN(numFee) ? numFee.toLocaleString() : fee;
  };
  
  const localFee = formatFee(program.estimated_fee_local);
  const internationalFee = formatFee(program.estimated_fee_international);
  const cashbackAmount = formatFee(program.cashback_amount);
  const hasCashback = program.cashback_amount && Number(program.cashback_amount) > 0;
  
  if (mobile) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 hover:border-[#F97316] hover:shadow-md transition-all relative overflow-hidden" data-testid="program-card">
        {isFeatured && (
          <div className="absolute top-0 right-0 z-10">
            <div className="bg-gradient-to-br from-[#059669] to-[#047857] text-white text-xs font-bold px-3 py-1 rounded-bl-lg shadow-md flex items-center gap-1">
              <span>⭐</span>
              <span>Featured</span>
            </div>
          </div>
        )}
        <div className={isFeatured ? 'pr-20 pt-8' : ''}>
          <h3 className="text-[#111827] font-semibold mb-1 line-clamp-2 text-sm leading-tight" style={{ fontWeight: 650, fontSize: '1.20rem' }}>
            {programName}
          </h3>
          <p className="text-[#4B5563] mb-2 font-semibold" style={{ fontSize: '0.80rem' }}>{universityName}</p>
        </div>
        
        <div className="flex flex-wrap gap-1.5 mb-2">
          {levelDisplay && (
            <span className="px-2 py-0.5 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded">
              {levelDisplay}
            </span>
          )}
          {modeDisplay && (
            <span className="px-2 py-0.5 bg-[#F8FAFC] text-[#4B5563] text-xs rounded border border-[#E5E7EB]">
              {modeDisplay}
            </span>
          )}
        </div>
        
        <div className="space-y-1.5 mb-2 text-xs">
          {duration && (
            <div className="flex items-center gap-1.5 text-[#4B5563]">
              <svg className="w-3 h-3 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">{duration}</span>
            </div>
          )}
          
          {localFee && (
            <div className="pt-1.5 border-t border-[#E5E7EB]">
              <p className="text-xs text-[#6B7280] mb-0.5">Local Student Fee</p>
              <p className="text-sm font-bold text-[#111827]">RM {localFee}</p>
            </div>
          )}
          
          {internationalFee && (
            <div className="pt-1">
              <p className="text-xs text-[#6B7280] mb-0.5">International Student Fee</p>
              <p className="text-sm font-bold text-[#111827]">RM {internationalFee}</p>
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap gap-1.5 mb-3">
          {hasCashback && (
            <span className="px-2 py-0.5 bg-[#D1FAE5] text-[#065F46] text-xs font-semibold rounded">
              💰 Cashback up to RM {cashbackAmount}
            </span>
          )}
          {program.scholarship_available && (
            <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-xs font-semibold rounded">
              🎓 Scholarship
            </span>
          )}
        </div>
        
        <button
          onClick={() => navigate(`/programs/${program.slug || program.id}`)}
          className="w-full px-3 py-2 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white text-xs font-semibold rounded-lg transition-all"
          data-testid="explore-program-button"
        >
          Explore Program →
        </button>
      </div>
    );
  }

  // Desktop card render
  return (
    <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-2.5 hover:border-[#F97316] hover:shadow-lg transition-all duration-200 flex flex-col h-full group relative overflow-hidden" data-testid="program-card">
      {isFeatured && (
        <div className="absolute top-0 right-0 z-10">
          <div className="bg-gradient-to-br from-[#059669] to-[#047857] text-white font-bold px-2 py-1 rounded-bl-lg shadow-md flex items-center gap-0.5" style={{ fontSize: '0.6rem' }}>
            <span>⭐</span>
            <span>Featured</span>
          </div>
        </div>
      )}
      <div className={isFeatured ? 'pr-12 pt-6' : ''}>
        <h3 className="text-[#111827] mb-0.5 line-clamp-2 group-hover:text-[#F97316] transition-colors leading-tight" style={{ fontSize: '1.2rem', fontWeight: 650, minHeight: '2.5rem' }}>
          {programName}
        </h3>
        <p className="text-[#4B5563] mb-1.5 font-semibold" style={{ fontSize: '0.80rem' }}>{universityName}</p>
      </div>
      
      <div className="flex flex-wrap gap-1 mb-2">
        {levelDisplay && (
          <span className="px-1.5 py-0.5 bg-[#E0F2FE] text-[#0369A1] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
            {levelDisplay}
          </span>
        )}
        {modeDisplay && (
          <span className="px-1.5 py-0.5 bg-[#F8FAFC] text-[#4B5563] rounded border border-[#E5E7EB]" style={{ fontSize: '0.65rem' }}>
            {modeDisplay}
          </span>
        )}
      </div>
      
      <div className="space-y-1.5 mb-2 text-[#4B5563]" style={{ fontSize: '0.65rem' }}>
        {duration && (
          <div className="flex items-center gap-1">
            <svg className="w-2.5 h-2.5 text-[#F97316]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">{duration}</span>
          </div>
        )}
        
        {localFee && (
          <div className="pt-1.5 border-t border-[#E5E7EB]">
            <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>Local Student Fee</p>
            <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>RM {localFee}</p>
          </div>
        )}
        
        {internationalFee && (
          <div className="pt-1">
            <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>International Student Fee</p>
            <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>RM {internationalFee}</p>
          </div>
        )}
      </div>
      
      <div className="flex flex-wrap gap-1 mb-2 mt-auto">
        {hasCashback && (
          <span className="px-1.5 py-0.5 bg-[#D1FAE5] text-[#065F46] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
            💰 Cashback up to RM {cashbackAmount}
          </span>
        )}
        {program.scholarship_available && (
          <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
            🎓 Scholarship
          </span>
        )}
      </div>
      
      <button
        onClick={() => navigate(`/programs/${program.slug || program.id}`)}
        className="w-full px-2.5 py-1.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all group-hover:scale-[1.02]"
        style={{ fontSize: '0.7rem' }}
        data-testid="explore-program-button"
      >
        Explore Program →
      </button>
    </div>
  );
};

export default Programs;
